
import { SimulationConfig, Ball, Vector2D, MorphData } from '../../types';
import { ShapeUtils } from '../physics/ShapeUtils';
import { MS_PER_TICK } from '../../constants';

export class RhythmicPhysics {
  public static calculateNextBounce(
    b: Ball,
    effectiveCfg: SimulationConfig,
    centerX: number,
    centerY: number,
    containerRadius: number,
    predictedRadius: number,
    morphData: MorphData,
    rotRad: number,
    visualTS: number,
    melodySpeed: number,
    duration: number,
    T: number,
    timeSinceCollisionMs: number,
    now: number,
    g: number,
    substeps: number,
    oldVel: Vector2D,
    worldNormal: Vector2D
  ) {
    const lineWidth = effectiveCfg.containerLineWidth || 4;
    const syncMode = effectiveCfg.rhythmicSyncMode || 'RADIUS_AND_SPEED';
    const X = b.pos.x - centerX;
    const Y = b.pos.y - centerY;
    const safeT = Math.min(T, 500); 
    const Yoffset = effectiveCfg.rhythmicSyncMode === 'CURVED_PATH' ? 0 : 0.5 * g * visualTS * visualTS * (safeT * safeT + safeT / substeps);
    const Yprime = Y + Yoffset;
    const currentV = Math.sqrt(b.vel.x*b.vel.x + b.vel.y*b.vel.y);
    const safeVisualTS = Math.max(0.01, visualTS);
    
    let Reff = (containerRadius - lineWidth / 2) - predictedRadius;
    Reff = Math.max(5, Reff);
    
    for (let iter = 0; iter < 2; iter++) {
        let chosenPhi = 0;
        let finalV = 0;
        let VT = 0;

        if (syncMode === 'DIRECTION_ONLY' || syncMode === 'CURVED_PATH') {
            const A = X; const B = Yprime;
            const distSq = A*A + B*B;
            const distToTargetCenter = Math.sqrt(distSq);
            const refSpeed = Math.max(currentV, 0.1);
            VT = refSpeed * T; 
            const minVT_physics = Math.abs(distToTargetCenter - Reff);
            const maxVT_physics = distToTargetCenter + Reff;
            if (VT < minVT_physics) VT = minVT_physics;
            if (VT > maxVT_physics) VT = maxVT_physics;
            if (effectiveCfg.rhythmicMinBounceDistance > 0 && VT < effectiveCfg.rhythmicMinBounceDistance) {
                VT = Math.min(effectiveCfg.rhythmicMinBounceDistance, maxVT_physics);
            }
            finalV = VT / T; 
            const C = (Reff*Reff - distSq - VT*VT) / (2 * VT);
            const mag = Math.sqrt(A*A + B*B);
            const cosVal = Math.max(-1, Math.min(1, C / mag));
            const angleOffset = Math.acos(cosVal);
            const baseAngle = Math.atan2(B, A);
            const phi1 = baseAngle + angleOffset;
            const phi2 = baseAngle - angleOffset;
            const dot = oldVel.x * worldNormal.x + oldVel.y * worldNormal.y;
            const refX = oldVel.x - 2 * dot * worldNormal.x;
            const refY = oldVel.y - 2 * dot * worldNormal.y;
            const refAngle = Math.atan2(refY, refX);
            const diff1 = Math.abs(Math.atan2(Math.sin(phi1-refAngle), Math.cos(phi1-refAngle)));
            const diff2 = Math.abs(Math.atan2(Math.sin(phi2-refAngle), Math.cos(phi2-refAngle)));
            chosenPhi = diff1 < diff2 ? phi1 : phi2;
        } else {
            const toCenterX = -X; const toCenterY = -Yprime;
            const distToCenter = Math.sqrt(toCenterX*toCenterX + toCenterY*toCenterY);
            const dirX = distToCenter > 0 ? toCenterX / distToCenter : 1;
            const dirY = distToCenter > 0 ? toCenterY / distToCenter : 0;
            const b_quad = 2 * (X * dirX + Yprime * dirY);
            const c_quad = X*X + Yprime*Yprime - Reff*Reff;
            const disc = b_quad*b_quad - 4 * c_quad;
            if (disc >= 0) {
                const z = Math.max((-b_quad + Math.sqrt(disc))/2, (-b_quad - Math.sqrt(disc))/2);
                VT = Math.max(5, z);
                if (effectiveCfg.rhythmicMinBounceDistance > 0 && VT < effectiveCfg.rhythmicMinBounceDistance) {
                    VT = Math.min(effectiveCfg.rhythmicMinBounceDistance, distToCenter + Reff);
                }
                finalV = Math.max(0.2, Math.min(300, (VT / T) / safeVisualTS));
                if (isNaN(finalV)) finalV = 10;
                chosenPhi = Math.atan2(dirY, dirX);
            } else {
                VT = distToCenter + Reff;
                if (effectiveCfg.rhythmicMinBounceDistance > 0 && VT < effectiveCfg.rhythmicMinBounceDistance) VT = effectiveCfg.rhythmicMinBounceDistance;
                finalV = (VT / T) / safeVisualTS;
                chosenPhi = Math.atan2(dirY, dirX);
            }
        }

        if (syncMode === 'DIRECTION_ONLY' || syncMode === 'CURVED_PATH' || effectiveCfg.rhythmicBallSpeedDecoupled) {
            let spatialSpeed = finalV * safeVisualTS;
            if (effectiveCfg.rhythmicMaxSpeed !== undefined && spatialSpeed > effectiveCfg.rhythmicMaxSpeed) spatialSpeed = effectiveCfg.rhythmicMaxSpeed;
            if (effectiveCfg.rhythmicMinSpeed !== undefined && spatialSpeed < effectiveCfg.rhythmicMinSpeed) spatialSpeed = effectiveCfg.rhythmicMinSpeed;
            finalV = spatialSpeed / safeVisualTS;
        }

        if (effectiveCfg.rhythmicSyncMode === 'CURVED_PATH') {
            const depth = effectiveCfg.rhythmicCurveDepth || 0.3;
            const h = depth * containerRadius;
            const theta = 4 * Math.atan(2 * h / VT);
            const S = VT * (theta / 2) / Math.sin(theta / 2);
            const alpha = effectiveCfg.rhythmicCurveSpeedProfile || 0;
            const T_sec = T * (MS_PER_TICK / 1000);
            const v_base = S / (T_sec * (1 + 2 * alpha / Math.PI));
            const centerAngle = Math.atan2(-Yprime, -X);
            const side = Math.atan2(Math.sin(centerAngle-chosenPhi), Math.cos(centerAngle-chosenPhi)) > 0 ? 1 : -1;
            const initialPhi = chosenPhi + side * (theta / 2);
            const v_base_tick = v_base * (MS_PER_TICK / 1000);
            b.vel.x = (v_base_tick / safeVisualTS) * Math.cos(initialPhi);
            b.vel.y = (v_base_tick / safeVisualTS) * Math.sin(initialPhi);
            b.curvature = (side * theta) / S;
            b.lastBounceTime = now - (timeSinceCollisionMs * safeVisualTS);
            b.nextBounceDuration = T_sec;
            b.curveBaseSpeed = v_base;
            b.originalAngle = initialPhi;
        } else {
            b.vel.x = finalV * Math.cos(chosenPhi);
            b.vel.y = finalV * Math.sin(chosenPhi);
            b.curvature = 0; b.lastBounceTime = undefined;
        }

        if (effectiveCfg.containerShape !== 'CIRCLE' || morphData.factor > 0) {
            const impactX = X + b.vel.x * safeVisualTS * T;
            const impactY = Y + b.vel.y * safeVisualTS * T + Yoffset;
            const shapeR = ShapeUtils.getInterpolatedRadiusAtAngle(Math.atan2(impactY, impactX) - rotRad, morphData.from, morphData.to, morphData.factor, containerRadius, morphData.baseRadius);
            Reff = (shapeR - lineWidth / 2) - predictedRadius;
        } else break;
    }
  }
}
